import {
  ProductSubscriptionModel,
  RecurringTypesEnum,
  SUBSCRIPTION_ACTIVE_STATUSES,
  SubscriptionProductModel,
  SubscriptionProductNamesEnum,
} from "dash-common";
import { SubscriptionProduct } from "gql/generated.graphql";
import { RoutesPath } from "flow-components/routes/routes";

export function getPricePerMonth(subscription: SubscriptionProduct) {
  if (subscription.recurringType === RecurringTypesEnum.SIXMONTH) {
    return subscription.price / 6;
  } else if (subscription.recurringType === RecurringTypesEnum.MONTH) {
    return subscription.price;
  } else if (subscription.recurringType === RecurringTypesEnum.YEAR) {
    return subscription.price / 12;
  } else {
    return 0;
  }
}

export const isLowerPricePerMonth = (
  newProduct: SubscriptionProduct,
  oldProduct: SubscriptionProduct
) => getPricePerMonth(newProduct) < getPricePerMonth(oldProduct);

export const isHigherPricePerMonth = (
  newProduct: SubscriptionProduct,
  oldProduct: SubscriptionProduct
) => getPricePerMonth(newProduct) > getPricePerMonth(oldProduct);

export function getChangeType(
  oldProduct?: SubscriptionProductModel,
  newProduct?: SubscriptionProductModel
) {
  if (!oldProduct || !newProduct) return "UPGRADE";

  if (
    oldProduct.stripeProductId === newProduct.stripeProductId &&
    oldProduct.price !== newProduct.price
  ) {
    return newProduct.recurringType === RecurringTypesEnum.MONTH
      ? "MONTH"
      : "SIXMONTH";
  }

  const oldPricePerMonth = getPricePerMonth(oldProduct);
  const newPricePerMonth = getPricePerMonth(newProduct);

  if (newPricePerMonth > oldPricePerMonth) {
    return "UPGRADE";
  } else if (newPricePerMonth < oldPricePerMonth) {
    return "DOWNGRADE";
  } else {
    return "UNCHANGED";
  }
}

// A paid plan that is still billing (or in trial). Anything else may start a
// fresh subscription through Checkout; this one must change in place instead.
export const hasLivePaidPlan = (
  subscription?: Pick<
    ProductSubscriptionModel,
    "status" | "subscriptionProduct"
  > | null
) =>
  !!subscription?.subscriptionProduct &&
  subscription.subscriptionProduct.price !== 0 &&
  SUBSCRIPTION_ACTIVE_STATUSES.includes(subscription.status);

export const getProductNameSlug = (product: SubscriptionProduct) => {
  return getStringSlug(product?.name);
};

export const getStringSlug = (value: string) => {
  return value?.toLowerCase().split(" ").join("-");
};

export const SUBSCRIPTION_PARAM = "subscription";

/**
 * DSH-853: where a buyer should land right after a successful purchase — Pro on
 * Portfolios, eXchange on the eXchange page. Accepts either a raw product name
 * ("DASH Exchange") or the slug that travels in the Stripe `?subscription=`
 * param ("dash-exchange"), so every success point can share one mapping.
 * Returns undefined for every other plan (API/Basic/Direct) and for an
 * absent/unknown value, which means "keep the caller's existing destination".
 */
export const getPostPurchaseRedirectPath = (
  productNameOrSlug?: string | null
): RoutesPath | undefined => {
  if (!productNameOrSlug) return undefined;

  const slug = getStringSlug(productNameOrSlug);

  if (slug === getStringSlug(SubscriptionProductNamesEnum.PRO)) {
    return RoutesPath.Portfolios;
  }

  if (slug === getStringSlug(SubscriptionProductNamesEnum.EXCHANGE)) {
    return RoutesPath.Exchange;
  }

  return undefined;
};

/**
 * DSH-853: Stripe's successUrl carries the purchased plan back to the app in
 * `?subscription=<slug>`. Built with the URL API instead of string concatenation
 * because the callers' pages can already have a query string (utm/gclid from an
 * ad, filters), and a second "?" silently swallowed the param — the returning
 * tab then had no idea what was bought.
 */
export const buildCheckoutSuccessUrl = (productSlug: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set(SUBSCRIPTION_PARAM, productSlug);
  return url.toString();
};
